import { CoreApi } from '@/state/api';
import {
  Center,
  Flex,
  Grid,
  GridItem,
  IconButton,
  Image,
  Text,
} from '@chakra-ui/react';
import { EmptyState } from '@ui';
import { ImNewTab } from 'react-icons/im';
import { LuTrash2 } from 'react-icons/lu';

const GRID_CARD_HEIGHT = 370;
const GRID_CARD_WIDTH = '300px';

export type AttachmentFile = {
  id: number;
  filename: string;
  file: string; // file url
};

type ViewAttachments = {
  attachments: AttachmentFile[];
  onDelete?: (props: AttachmentFile) => void;
  onSelect?: (props: AttachmentFile) => void;
};

const ViewAttachments = ({
  attachments,
  onDelete,
  onSelect,
}: ViewAttachments) => {
  if (!attachments?.length) {
    return (
      <Center width="full">
        <EmptyState
          isEmpty
          position="relative"
          title="No attachments available"
          description="Please upload new attachments to view them here"
        />
      </Center>
    );
  }

  return (
    <Grid
      templateColumns={`repeat(auto-fit, minmax(${GRID_CARD_WIDTH}, ${GRID_CARD_WIDTH}))`}
      gap={4}
      width="full"
    >
      {attachments?.map((item, index) => (
        <GridItem
          key={`attachment-${item?.id}-${item?.filename}-${item?.file}-${index}`}
        >
          <ViewAttachmentsCard
            filename={item?.filename}
            file={item?.file}
            onDelete={onDelete ? () => onDelete(item) : undefined}
            onSelect={onSelect ? () => onSelect(item) : undefined}
          />
        </GridItem>
      ))}
    </Grid>
  );
};

export default ViewAttachments;

type ViewAttachmentsCard = {
  filename: string;
  file: string;
  onDelete?: () => void;
  onSelect?: () => void;
};
export const ViewAttachmentsCard = ({
  filename,
  file,
  onDelete,
  onSelect,
}: ViewAttachmentsCard) => {
  const { data: fileType } = CoreApi.Api.useGetAttachmentFileTypeQuery({
    url: file,
  });

  const previewFile = () => {
    if (fileType?.startsWith('image/')) {
      return (
        <Image
          src={file}
          alt={filename}
          height="full"
          width="full"
          objectFit="cover"
        />
      );
    }

    return (
      <embed
        src={`${file}#toolbar=0&navpanes=0&scrollbar=0&page=1&zoom=page-cover`}
        type="application/pdf"
        width="full"
        height={GRID_CARD_HEIGHT}
        style={{ pointerEvents: 'none', objectFit: 'contain' }}
      />
    );
  };

  return (
    <Flex
      // width={GRID_CARD_WIDTH}
      // height={GRID_CARD_HEIGHT + 50}
      transition="transform 0.3s ease-in-out"
      transform="scale(1)"
      _hover={{
        transform: 'scale(1.05)',
      }}
      position="relative"
      className="group"
    >
      {/* Delete button */}

      <Flex
        flexDirection="column"
        gapY={2}
        position="absolute"
        top={2}
        right={2}
        zIndex={99}
      >
        {onDelete && (
          <IconButton
            aria-label="Delete attachment"
            bg="fg.error"
            color="fg"
            opacity={0}
            transition="opacity 0.3s ease-in-out"
            _groupHover={{ opacity: 1 }}
            _focusVisible={{ opacity: 1 }}
            onClick={onDelete}
          >
            <LuTrash2 />
          </IconButton>
        )}

        <IconButton
          aria-label="Open attachment in new tab"
          colorPalette="blue"
          opacity={0}
          transition="opacity 0.3s ease-in-out"
          _groupHover={{ opacity: 1 }}
          _focusVisible={{ opacity: 1 }}
          zIndex={1}
          onClick={() => window.open(file, '_blank')}
        >
          <ImNewTab />
        </IconButton>
      </Flex>

      <Flex flexDirection="column" gapY={4} onClick={onSelect}>
        {/* File preview */}
        <Flex height={GRID_CARD_HEIGHT}>{previewFile()}</Flex>

        {/* File name */}
        <Flex bottom="0">
          <Text whiteSpace="break-spaces" wordBreak="break-word">
            {filename}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
};
